import { vaultFetch, qs } from './vault-api-core.ts'

export type VaultApprovalState = 'pending' | 'approved' | 'denied' | 'expired'
export type VaultApprovalMatch = 'exact' | 'prefix'
export type VaultWindowMinutes = 1 | 5 | 30 | 60

/**
 * Which of ADR 0008's three answers the approver gave.
 *
 * Meaningful ONLY when `state === 'approved'`. Every other state carries
 * the `'window'` default straight off the column, so rendering it on a
 * pending or denied row would invent a decision nobody made.
 */
export type VaultApprovalResolution = 'window' | 'adhoc_value' | 'human_ran_it'

/** What the console is told about an ad-hoc value — never the value, never
 * its ciphertext, nonce or wrapped DEK. `consumedAt` is the field that
 * earns this object: an approver needs to see whether the one shot they
 * authorised has already been taken. */
export interface VaultAdhocSummary {
  itemKey: string
  expiresAt: string
  consumedAt: string | null
}

export interface VaultApprovalRow {
  id: string
  principalId: string
  principalName: string
  sessionId: string | null
  sessionTitle: string | null
  nodeIds: string[]
  nodePaths: string[]
  environment: string
  itemKeys: string[]
  argv: string[]
  argvHash: string
  match: VaultApprovalMatch
  state: VaultApprovalState
  resolution: VaultApprovalResolution
  /**
   * The agent's stated intent, or `''`.
   *
   * UNTRUSTED TEXT AN AGENT WROTE. Render it as text — never as markup,
   * never into a `title`/`href`/`dangerouslySetInnerHTML`. React escapes a
   * string child, which is the whole defence, and it only holds while this
   * stays a string child.
   */
  reason: string
  windowMinutes: number | null
  approvedBy: string | null
  decidedAt: string | null
  expiresAt: string | null
  createdAt: string
  adhoc: VaultAdhocSummary | null
  [key: string]: unknown
}

/**
 * The approver's own value, sealed in THIS browser before it is posted.
 *
 * Built by `sealValueForTenant` plus the request's item key and the root's
 * `currentKekVersion`. Sealing uses the tenant PUBLIC half only, so it
 * works whether or not the tenant key is unlocked, and the plaintext never
 * leaves the page — the server stores these four fields verbatim and hands
 * them back to the daemon as an ordinary `RunKeyOut`.
 */
export interface VaultAdhocSealed {
  /** The environment variable name the daemon will inject. Normally the
   * request's single `itemKeys` entry. */
  itemKey: string
  ciphertext: string
  nonce: string
  wrappedDek: string | null
  /** A wrong version fails later on the daemon with "kek version
   * mismatch", so it is read from `GET …/tenant-key` rather than guessed. */
  kekVersion: number | null
}

/**
 * The decide body, as the same union of four strict shapes the API
 * validates.
 *
 * A union rather than a bag of optional fields because the server rejects
 * unknown keys per arm: an `adhoc` posted alongside a `windowMinutes` is a
 * `validation_failed`, not a quietly-stripped field. Typing it this way
 * means that mistake does not compile either.
 */
export type VaultApprovalDecideBody =
  | { decision: 'denied' }
  | {
      decision: 'approved'
      resolution?: 'window'
      windowMinutes?: VaultWindowMinutes
      match?: VaultApprovalMatch
    }
  | {
      decision: 'approved'
      resolution: 'adhoc_value'
      adhoc: VaultAdhocSealed
      expiresInMinutes?: VaultWindowMinutes
    }
  | { decision: 'approved'; resolution: 'human_ran_it' }

/** One page of the queue, newest first. `nextBefore` is the cursor for the
 * following page, or `null` when this page was the last one. */
export interface VaultApprovalsPage {
  requests: VaultApprovalRow[]
  nextBefore: string | null
}

export interface ListApprovalsOptions {
  state?: VaultApprovalState
  limit?: number
  /** A request id from a previous page's `nextBefore`. */
  before?: string
}

// ── approvals ───────────────────────────────────────────────────────────
/**
 * One page of approval requests, newest first (uuidv7 id DESC).
 *
 * Was unpaginated and createdAt-ASC, which meant the oldest decided
 * requests in the root loaded first and the thing an operator came for —
 * what is waiting NOW — was at the bottom of an ever-growing list.
 */
export function listApprovals(
  rootId: string,
  options?: ListApprovalsOptions,
): Promise<VaultApprovalsPage> {
  return vaultFetch<VaultApprovalsPage>(
    `/vault/admin/${rootId}/approvals${qs({
      state: options?.state,
      limit: options?.limit?.toString(),
      before: options?.before,
    })}`,
  )
}

/** One request by id. A request belonging to another root answers 404, not
 * 403 — membership in one root must not be usable to probe another's ids. */
export function getApproval(
  rootId: string,
  id: string,
): Promise<VaultApprovalRow> {
  return vaultFetch<VaultApprovalRow>(`/vault/admin/${rootId}/approvals/${id}`)
}

export function decideApproval(
  rootId: string,
  id: string,
  body: VaultApprovalDecideBody,
): Promise<VaultApprovalRow> {
  return vaultFetch<VaultApprovalRow>(
    `/vault/admin/${rootId}/approvals/${id}/decide`,
    { method: 'POST', body: JSON.stringify(body) },
  )
}
